import React, { useEffect, useState } from 'react';
import { useResumeContext } from '../../context/ResumeContext';
import SkillMeter from '../shared/SkillMeter';
import SkillCategoryChart from '../shared/SkillCategoryChart';
import { skillsData } from '../../data/skillsData';

/**
 * Competency Assessment Module Component
 * Shows skills across different categories with filterable view
 */
const CompetencyModule = () => {
  const { markSectionViewed } = useResumeContext();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [skillLevel, setSkillLevel] = useState('all');
  
  useEffect(() => {
    markSectionViewed('competency');
  }, [markSectionViewed]);

  // Skill categories with display names
  const categories = [
    { id: 'all', name: 'All Skills' },
    { id: 'instructionalSkills', name: 'Instructional Design' },
    { id: 'technicalSkills', name: 'Technical' },
    { id: 'professionalSkills', name: 'Professional' }
  ];

  // Level filters
  const levels = [
    { id: 'all', name: 'All Levels' },
    { id: 'expert', name: 'Expert (90-100%)', min: 90, max: 100 },
    { id: 'advanced', name: 'Advanced (80-89%)', min: 80, max: 89 },
    { id: 'proficient', name: 'Proficient (70-79%)', min: 70, max: 79 },
    { id: 'developing', name: 'Developing (< 70%)', min: 0, max: 69 }
  ];

  // Filter skills based on active category, search query, and skill level
  const filteredSkills = Object.entries(skillsData)
    .flatMap(([category, skills]) => {
      // Only include skills from the selected category (or all if 'all' is selected)
      if (activeCategory !== 'all' && category !== activeCategory) {
        return [];
      }
      
      return skills.filter(skill => {
        // Filter by search query
        const matchesSearch = searchQuery === '' || 
          skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          skill.description.toLowerCase().includes(searchQuery.toLowerCase());
        
        // Filter by skill level
        const matchesLevel = skillLevel === 'all' || 
          (skill.level >= levels.find(l => l.id === skillLevel)?.min && 
           skill.level <= levels.find(l => l.id === skillLevel)?.max);
        
        return matchesSearch && matchesLevel;
      }).map(skill => ({
        ...skill,
        category
      }));
    })
    // Sort by level (highest first)
    .sort((a, b) => b.level - a.level);

  // Function to get category display name
  const getCategoryName = (categoryId) => {
    return categories.find(cat => cat.id === categoryId)?.name || categoryId;
  };

  // Skill counts by category
  const skillCounts = {
    all: Object.values(skillsData).flat().length,
    instructionalSkills: skillsData.instructionalSkills.length,
    technicalSkills: skillsData.technicalSkills.length,
    professionalSkills: skillsData.professionalSkills.length
  };

  return (
    <div className="space-y-6">
      <section className="card border-l-4 border-orange-500">
        <h2 className="text-xl font-bold text-orange-700 mb-4 flex items-center gap-2">
          📊 Competency Assessment
        </h2>
        <p className="mb-6">
          This assessment represents a self-evaluation of my skills relevant to instructional design 
          and learning content development. Each skill is rated based on my demonstrated experience 
          and proficiency in professional settings.
        </p>
        
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-sm">Expert (90-100%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-sm">Advanced (80-89%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-sm">Proficient (70-79%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-orange-500 rounded"></div>
            <span className="text-sm">Developing (50-69%)</span>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="card">
        <h3 className="text-lg font-bold mb-4">Filter Skills</h3>
        
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          {/* Category Filter */}
          <div className="flex-1">
            <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Skill Category
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    activeCategory === category.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  {category.name} ({skillCounts[category.id]})
                </button>
              ))}
            </div>
          </div>
          
          {/* Level Filter */}
          <div className="flex-1">
            <label htmlFor="level-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Skill Level
            </label>
            <div className="flex flex-wrap gap-2">
              {levels.map(level => (
                <button
                  key={level.id}
                  onClick={() => setSkillLevel(level.id)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    skillLevel === level.id
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  {level.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Search */}
        <div className="mb-4">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search Skills
          </label>
          <input
            type="text"
            id="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by skill name or description..."
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </section>

      {/* Results Section */}
      <section className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">
            {filteredSkills.length} {filteredSkills.length === 1 ? 'Skill' : 'Skills'} Found
          </h3>
          {filteredSkills.length > 0 && activeCategory !== 'all' && (
            <span className="text-sm text-slate-600">
              Showing {getCategoryName(activeCategory)} skills
            </span>
          )}
        </div>

        {filteredSkills.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <p>No skills match your current filters.</p>
            <button 
              onClick={() => {
                setActiveCategory('all');
                setSkillLevel('all');
                setSearchQuery('');
              }}
              className="mt-2 px-4 py-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSkills.map((skill) => (
              <div key={`${skill.category}-${skill.name}`} className="relative">
                {/* Category Badge */}
                <div className="absolute -top-2 -right-2 z-10">
                  <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full
                    ${skill.category === 'instructionalSkills' ? 'bg-orange-100 text-orange-800' : 
                      skill.category === 'technicalSkills' ? 'bg-blue-100 text-blue-800' : 
                      'bg-purple-100 text-purple-800'}`}
                  >
                    {getCategoryName(skill.category).split(' ')[0]}
                  </span>
                </div>
                
                <SkillMeter 
                  name={skill.name}
                  level={skill.level}
                  description={skill.description}
                  examples={skill.examples}
                />
              </div>
            ))}
          </div>
        )}
      </section>
      
      {/* Skill Growth Visualization */}
      <section className="card">
        <h3 className="text-lg font-bold mb-4">Skill Development Analysis</h3>
        <p className="text-slate-600 mb-4">
          This visualization shows how my skills have developed across six key competency areas 
          throughout my career journey. Each category represents a set of related skills that 
          contribute to my ability to transform complex technical information into accessible 
          learning experiences.
        </p>
        <SkillCategoryChart />
      </section>
    </div>
  );
};

export default CompetencyModule;